import { useCallback } from 'react';
import { DateTime } from 'luxon';
import { DateObject } from 'react-multi-date-picker';
import { useFormContext } from 'react-hook-form';
import { useCalendarDefaults } from 'shared/hooks/useCalendarDefaults';
import { WorkFormContext } from 'shared/components/work-forms/work-form.types';
import { MULTI_DAY_WORK_FORM_FIELDS } from 'shared/components/work-forms/multi-day/multi-day-work.types';
import { useMultiDaySync } from 'shared/components/work-forms/multi-day/hooks/use-multi-day-sync';
import { dateTimeToDateObject } from 'shared/components/shift-forms/components/shift-multiply-calendar/multi-date-object-utils';
import {
  dateObjectsToIsoDateStrings,
  getCalendarMinAvailableDate,
  isoDateStringsToDateObjects,
} from './multi-day-dates-calendar.helpers';
import { MultiDayDatesCalendarProps } from './multi-day-dates-calendar.types';

export const useMultiDayDatesCalendar = ({ allowsPast }: MultiDayDatesCalendarProps) => {
  const { watch } = useFormContext<WorkFormContext>();
  const { syncDates } = useMultiDaySync();
  const selectedDates = watch(MULTI_DAY_WORK_FORM_FIELDS.multiDayDates) ?? [];
  const { monthLabels, dayLabels } = useCalendarDefaults();
  const today = DateTime.now().startOf('day');
  const minAvailableDate = getCalendarMinAvailableDate(allowsPast, today);

  const onChange = useCallback(
    (date: DateObject | DateObject[]) => {
      if (!date) {
        return;
      }

      const nextDates = dateObjectsToIsoDateStrings(Array.isArray(date) ? date : [date]);
      syncDates(nextDates);
    },
    [syncDates]
  );

  return {
    dayLabels,
    minDate: minAvailableDate ? dateTimeToDateObject(minAvailableDate) : undefined,
    monthLabels,
    onChange,
    selectedDates: isoDateStringsToDateObjects(selectedDates),
    today: dateTimeToDateObject(today),
  };
};
